import Vue from "vue";
import Router from "vue-router";
import Layout from "@/layout/index.vue";
import store from "@/store";
import { buildDocumentTitle } from "@/utils/page-title";
import { resolveLoginNavigation } from "@/utils/router-login-guard";

Vue.use(Router);

const LoginIndex = () => import("@/views/login/index.vue");
const LoginPasswordSetup = () => import("@/views/login/password-setup.vue");
const DashboardIndex = () => import("@/views/dashboard/index.vue");
const SupplierCreate = () => import("@/views/suppliers/create.vue");
const SupplierList = () => import("@/views/suppliers/list.vue");
const SupplierPaymentConfig = () => import("@/views/suppliers/payment-config.vue");
const SchoolCreate = () => import("@/views/schools/create.vue");
const SchoolList = () => import("@/views/schools/list.vue");
const SchoolDetail = () => import("@/views/schools/detail.vue");
const SchoolImportOrders = () => import("@/views/schools/import-orders.vue");
const TextbookCreate = () => import("@/views/textbooks/create.vue");
const TextbookList = () => import("@/views/textbooks/list.vue");
const OrderOverview = () => import("@/views/orders/overview.vue");
const OrderList = () => import("@/views/orders/list.vue");
const OrderVerify = () => import("@/views/orders/verify.vue");
const AnalyticsTerm = () => import("@/views/analytics/term.vue");
const AnalyticsSuppliers = () => import("@/views/analytics/suppliers.vue");
const AnalyticsSchools = () => import("@/views/analytics/schools.vue");
const SupplierDashboardIndex = () => import("@/views/supplier/dashboard/index.vue");
const SupplierSchoolCreate = () => import("@/views/supplier/schools/create.vue");
const SupplierSchoolList = () => import("@/views/supplier/schools/list.vue");
const SupplierSchoolDetail = () => import("@/views/supplier/schools/detail.vue");
const SupplierSchoolImportOrders = () => import("@/views/supplier/schools/import-orders.vue");
const SupplierTextbookCreate = () => import("@/views/supplier/textbooks/create.vue");
const SupplierTextbookList = () => import("@/views/supplier/textbooks/list.vue");
const SupplierOrderOverview = () => import("@/views/supplier/orders/overview.vue");
const SupplierOrderSummary = () => import("@/views/supplier/orders/summary.vue");
const SupplierOrderList = () => import("@/views/supplier/orders/list.vue");
const SupplierOrderPrintSlip = () => import("@/views/supplier/orders/print-slip.vue");
const SupplierOrderVerify = () => import("@/views/supplier/orders/verify.vue");
const SupplierAnalyticsTerm = () => import("@/views/supplier/analytics/term.vue");
const SupplierAnalyticsSchools = () => import("@/views/supplier/analytics/schools.vue");
const SchoolDashboardIndex = () => import("@/views/school/dashboard/index.vue");
const SchoolStudentsImport = () => import("@/views/school/students/import.vue");
const SchoolStudentsList = () => import("@/views/school/students/list.vue");
const SchoolStudentsRebind = () => import("@/views/school/students/rebind.vue");
const SchoolCoursesImport = () => import("@/views/school/courses/import.vue");
const SchoolCoursesList = () => import("@/views/school/courses/list.vue");
const SchoolTextbooksImport = () => import("@/views/school/textbooks/import.vue");
const SchoolTextbooksList = () => import("@/views/school/textbooks/list.vue");
const SchoolAnalyticsOverview = () => import("@/views/school/analytics/overview.vue");

const withMeta = (role, title, section) => ({ role, title, section });

const routes = [
  {
    path: "/",
    redirect: "/platform/dashboard"
  },
  {
    path: "/login",
    name: "AdminLogin",
    component: LoginIndex,
    meta: {
      title: "统一登录"
    }
  },
    {
      path: "/login/password-setup",
      name: "AdminPasswordSetup",
      component: LoginPasswordSetup,
      meta: {
        title: "设置密码"
      }
    },
  {
    path: "/platform",
    component: Layout,
    children: [
      { path: "dashboard", name: "PlatformDashboard", component: DashboardIndex, meta: withMeta("platform", "首页", "首页") },
      { path: "suppliers/create", name: "PlatformSupplierCreate", component: SupplierCreate, meta: withMeta("platform", "新增供应商", "供应商") },
      { path: "suppliers/list", name: "PlatformSupplierList", component: SupplierList, meta: withMeta("platform", "供应商列表", "供应商") },
      { path: "suppliers/payment-config", name: "PlatformSupplierPaymentConfig", component: SupplierPaymentConfig, meta: withMeta("platform", "支付配置", "供应商") },
      { path: "schools/create", name: "PlatformSchoolCreate", component: SchoolCreate, meta: withMeta("platform", "新增学校", "学校") },
      { path: "schools/list", name: "PlatformSchoolList", component: SchoolList, meta: withMeta("platform", "学校列表", "学校") },
      { path: "schools/:id", name: "PlatformSchoolDetail", component: SchoolDetail, meta: withMeta("platform", "学校详情", "学校") },
      { path: "schools/:id/import-orders", name: "PlatformSchoolImportOrders", component: SchoolImportOrders, meta: withMeta("platform", "导入教材征订单", "学校") },
      { path: "textbooks/create", name: "PlatformTextbookCreate", component: TextbookCreate, meta: withMeta("platform", "新增教材", "教材") },
      { path: "textbooks/list", name: "PlatformTextbookList", component: TextbookList, meta: withMeta("platform", "教材列表", "教材") },
      { path: "orders/overview", name: "PlatformOrderOverview", component: OrderOverview, meta: withMeta("platform", "订单概览", "订单") },
      { path: "orders/list", name: "PlatformOrderList", component: OrderList, meta: withMeta("platform", "订单列表", "订单") },
      { path: "orders/verify", name: "PlatformOrderVerify", component: OrderVerify, meta: withMeta("platform", "订单核销", "订单") },
      { path: "analytics/term", name: "PlatformAnalyticsTerm", component: AnalyticsTerm, meta: withMeta("platform", "按学期统计", "统计与分析") },
      { path: "analytics/suppliers", name: "PlatformAnalyticsSuppliers", component: AnalyticsSuppliers, meta: withMeta("platform", "按供应商统计", "统计与分析") },
      { path: "analytics/schools", name: "PlatformAnalyticsSchools", component: AnalyticsSchools, meta: withMeta("platform", "按学校统计", "统计与分析") }
    ]
  },
  {
    path: "/supplier",
    component: Layout,
    children: [
      { path: "dashboard", name: "SupplierDashboard", component: SupplierDashboardIndex, meta: withMeta("supplier", "首页", "首页") },
      { path: "schools/create", name: "SupplierSchoolsCreate", component: SupplierSchoolCreate, meta: withMeta("supplier", "新增学校", "学校") },
      { path: "schools/list", name: "SupplierSchoolsList", component: SupplierSchoolList, meta: withMeta("supplier", "学校列表", "学校") },
      { path: "schools/:id", name: "SupplierSchoolsDetail", component: SupplierSchoolDetail, meta: withMeta("supplier", "学校详情", "学校") },
      { path: "schools/:id/import-orders", name: "SupplierSchoolsImportOrders", component: SupplierSchoolImportOrders, meta: withMeta("supplier", "导入教材征订单", "学校") },
      { path: "textbooks/create", name: "SupplierTextbooksCreate", component: SupplierTextbookCreate, meta: withMeta("supplier", "新增教材", "教材") },
      { path: "textbooks/list", name: "SupplierTextbooksList", component: SupplierTextbookList, meta: withMeta("supplier", "教材列表", "教材") },
      { path: "orders/overview", name: "SupplierOrdersOverview", component: SupplierOrderOverview, meta: withMeta("supplier", "订单概览", "订单") },
      { path: "orders/summary", name: "SupplierOrdersSummary", component: SupplierOrderSummary, meta: withMeta("supplier", "订单汇总", "订单") },
      { path: "orders/list", name: "SupplierOrdersList", component: SupplierOrderList, meta: withMeta("supplier", "订单列表", "订单") },
      { path: "orders/print-slip", name: "SupplierOrdersPrintSlip", component: SupplierOrderPrintSlip, meta: withMeta("supplier", "打印领书单", "订单") },
      { path: "orders/verify", name: "SupplierOrdersVerify", component: SupplierOrderVerify, meta: withMeta("supplier", "订单核销", "订单") },
      { path: "analytics/term", name: "SupplierAnalyticsTerm", component: SupplierAnalyticsTerm, meta: withMeta("supplier", "按学期统计", "统计与分析") },
      { path: "analytics/schools", name: "SupplierAnalyticsSchools", component: SupplierAnalyticsSchools, meta: withMeta("supplier", "按学校统计", "统计与分析") }
    ]
  },
  {
    path: "/school",
    component: Layout,
    children: [
      { path: "dashboard", name: "SchoolDashboard", component: SchoolDashboardIndex, meta: withMeta("school", "首页", "首页") },
      { path: "students/import", name: "SchoolStudentsImport", component: SchoolStudentsImport, meta: withMeta("school", "新增学生", "学生") },
      { path: "students/list", name: "SchoolStudentsList", component: SchoolStudentsList, meta: withMeta("school", "学生列表", "学生") },
      { path: "students/rebind", name: "SchoolStudentsRebind", component: SchoolStudentsRebind, meta: { ...withMeta("school", "学生换绑审批", "学生"), roles: ["school_admin"] } },
      { path: "courses/import", name: "SchoolCoursesImport", component: SchoolCoursesImport, meta: withMeta("school", "新增课程", "课程") },
      { path: "courses/list", name: "SchoolCoursesList", component: SchoolCoursesList, meta: withMeta("school", "课程列表", "课程") },
      { path: "textbooks/import", name: "SchoolTextbooksImport", component: SchoolTextbooksImport, meta: withMeta("school", "教材征订", "教材") },
      { path: "textbooks/list", name: "SchoolTextbooksList", component: SchoolTextbooksList, meta: withMeta("school", "教材列表", "教材") },
      { path: "analytics/overview", name: "SchoolAnalyticsOverview", component: SchoolAnalyticsOverview, meta: withMeta("school", "统计概览", "统计与分析") }
    ]
  }
];

const router = new Router({
  mode: "hash",
  routes
});

router.beforeEach((to, _from, next) => {
  const decision = resolveLoginNavigation(to, typeof window !== "undefined" ? window.localStorage : null);
  if (decision.role) {
    store.commit("platform/SET_ROLE", decision.role);
  }

  if (!decision.allow) {
    next(decision.redirect);
    return;
  }

  next();
});

router.afterEach((to) => {
  if (typeof document === "undefined") {
    return;
  }

      if (String(to.path || "").startsWith("/login")) {
    document.title = buildDocumentTitle({
          pageTitle: to.meta?.title || "统一登录"
    });
    return;
  }

  const role = to.meta?.role || store.state.platform.currentRole || "platform";
  const roleMeta = store.state.platform.roles[role] || {};
  document.title = buildDocumentTitle({
    pageTitle: to.meta?.title || "",
    roleLabel: roleMeta.label || ""
  });
});

export default router;
